import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Stack } from "@phosphor-icons/react";

/**
 * TobInputModal (legacy filename FlowRateInputModal.jsx) — supervisor logs the
 * current Tons On Board. The backend derives the live flow rate as
 * (new_tob - prior_tob) ÷ running hours (excluding pauses).
 */
export default function FlowRateInputModal({ vessel, onClose, onSubmitted }) {
    const [tob, setTob] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [priorTob, setPriorTob] = useState(0);
    const [priorAt, setPriorAt] = useState(null);
    // Live tonnage — always re-fetched on open so that lineup edits made
    // after the live page was loaded apply immediately (no refresh needed).
    const [tonnage, setTonnage] = useState(Number(vessel?.tonnage || 0));

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/vessels/${vessel.id}/flow-rate`);
                if (cancel) return;
                const lastWithTob = [...(data || [])].reverse().find((d) => typeof d.tob === "number");
                if (lastWithTob) {
                    setPriorTob(Number(lastWithTob.tob || 0));
                    setPriorAt(lastWithTob.created_at);
                } else {
                    setPriorTob(0);
                    setPriorAt(vessel?.start_datetime || null);
                }
            } catch {
                /* ignore */
            }
            try {
                const { data } = await api.get(`/vessels/${vessel.id}/metrics`);
                if (cancel) return;
                if (data && typeof data.tonnage === "number") setTonnage(Number(data.tonnage));
            } catch {
                /* ignore — fall back to vessel prop */
            }
        })();
        return () => {
            cancel = true;
        };
    }, [vessel.id, vessel?.start_datetime]);

    const parsed = parseFloat(tob);
    const delta = !Number.isNaN(parsed) ? parsed - priorTob : 0;
    const elapsedMin = priorAt
        ? Math.max(0, (Date.now() - new Date(priorAt).getTime()) / 60000)
        : 0;
    const elapsedLabel = elapsedMin >= 60
        ? `${Math.floor(elapsedMin / 60)}h ${Math.round(elapsedMin % 60)}m`
        : `${Math.round(elapsedMin)}m`;

    const submit = async (e) => {
        e.preventDefault();
        if (Number.isNaN(parsed) || parsed < 0) {
            toast.error("Enter a valid Tons On Board value");
            return;
        }
        if (tonnage && parsed > tonnage * 1.05) {
            toast.error(`TOB cannot exceed vessel tonnage (${tonnage.toLocaleString()} t)`);
            return;
        }
        if (delta < 0) {
            toast.error(`TOB cannot decrease (previous reading: ${priorTob.toLocaleString()} t)`);
            return;
        }
        setLoading(true);
        try {
            await api.post(`/vessels/${vessel.id}/tob`, { tob: parsed, note: note || null });
            toast.success("Tons On Board updated");
            onSubmitted && onSubmitted();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="flow-rate-modal">
            <div className="g-card w-full max-w-md overflow-hidden">
                <div className="g-card-header-dark">
                    <Stack size={18} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Update Tons On Board</div>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="flow-rate-close">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="rounded-sm bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                        <div>Previous TOB: <span className="tabular font-semibold text-[var(--grindrod-navy)]">{priorTob.toLocaleString()} t</span></div>
                        <div>Logged: <span className="tabular font-semibold text-[var(--grindrod-navy)]">{priorAt ? `${elapsedLabel} ago` : "—"}</span></div>
                        <div className="mt-1 text-slate-500">
                            Live flow rate is auto-derived from the change in TOB over running time (pauses excluded).
                        </div>
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">Tons on board <span className="text-slate-400 normal-case lowercase tracking-normal">(current)</span></label>
                        <input
                            type="number"
                            min="0"
                            max={tonnage ? tonnage * 1.05 : 200000}
                            step="any"
                            value={tob}
                            onChange={(e) => setTob(e.target.value)}
                            placeholder={`e.g. ${Math.round(priorTob + 1500).toLocaleString()}`}
                            className="g-input tabular text-2xl font-bold"
                            autoFocus
                            data-testid="flow-rate-input"
                        />
                        {!Number.isNaN(parsed) && parsed >= 0 && (
                            <div className="mt-2 text-[11px] tabular text-slate-600 flex items-center gap-3">
                                <span>Δ TOB: <span className={`font-semibold ${delta >= 0 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>{delta >= 0 ? "+" : ""}{Math.round(delta).toLocaleString()} t</span></span>
                                <span className="text-slate-300">·</span>
                                <span>Over <span className="font-semibold">{elapsedLabel}</span></span>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">Note <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></label>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. After hatch change"
                            className="g-input"
                            data-testid="flow-rate-note"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="flow-rate-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="flow-rate-submit">
                            {loading ? "Updating…" : "Update TOB"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
